import type {BmContractStatusColorSource} from './bm-contract-status-color-source';

/**
 * AI:
 * Status: "in progress"
 * Type: Logic
 * SubType: Display style
 * Reason: The colours of the tags in the status column of the Verträge tab of a billing account - the
 *   contract's base status in the colours of its own enum (`cm.ContractBaseStatusEnum`, delivered on the
 *   contract DTO as `baseStatusEnumColorBackground` / `baseStatusEnumColor`), plus the fallback status
 *   tags Aktiv/Beendet and the assignment tags „ab {Datum}" / „bis {Datum}".
 *
 *   WHY A styleClass AND NOT [severity]: [severity] is INERT app-wide.
 *   primeng-v20-overrides.scss declares `.p-tag { background: var(--primary-800) }` unconditionally, at
 *   the same specificity as PrimeNG's generated `.p-tag-success/-danger/…` and loaded after them, so
 *   every tag renders primary-800 blue whatever [severity] says (see the dunning tag in the header of
 *   bm-customer-account-object-full.component.html). The PrimeFlex colour utilities carry !important
 *   and win. Same mechanism as the phase tag of the open applications in the same tab
 *   (`phaseStyleClass`, bm-open-cw-case-lines-ql.ts) - typography first, then `bg-{token} text-{token}`,
 *   a neutral grey pair when a token is missing or malformed.
 *
 *   A token is accepted only in the shape the backend's `@UiEnumValueInfo` stores it (`green-800`): the
 *   value ends up in a class attribute, so anything else (HEX, a full class name, free text) falls back
 *   to the neutral pair instead of producing an unknown or foreign class.
 *
 * @ticket TM-1000002026
 */

/** Typography of every tag of the status column - the first half of each style class. */
export const BM_CONTRACT_TAG_BASE_CLASS = 'text-xs font-bold';

/** The colour pair of a tag whose enum brought no usable colours. */
export const BM_CONTRACT_TAG_NEUTRAL_COLORS = 'bg-gray-200 text-gray-800';

/** A PrimeFlex palette token as the backend's `@UiEnumValueInfo` stores it, e.g. `green-800`. */
const BM_PALETTE_TOKEN_PATTERN = /^[a-z]+-\d{2,3}$/;

/** Whether a value is a usable PrimeFlex palette token (`green-200`, `purple-800`, …). */
export function isBmPaletteToken(value: unknown): value is string {
    return typeof value === 'string' && BM_PALETTE_TOKEN_PATTERN.test(value);
}

/**
 * The complete tag style class: the typography plus `bg-{background} text-{color}` from two palette
 * tokens, or plus the neutral pair when either token is unusable.
 */
export function bmEnumTagStyleClass(background: unknown, color: unknown): string {
    const colours = isBmPaletteToken(background) && isBmPaletteToken(color)
        ? `bg-${background} text-${color}`
        : BM_CONTRACT_TAG_NEUTRAL_COLORS;
    return `${BM_CONTRACT_TAG_BASE_CLASS} ${colours}`;
}

/** The style class of a contract's base status tag, in the colours of the status enum. */
export function bmContractStatusTagStyleClass(contract: BmContractStatusColorSource | null | undefined): string {
    return bmEnumTagStyleClass(contract?.baseStatusEnumColorBackground, contract?.baseStatusEnumColor);
}

/** Fallback tag „Aktiv" (no base status delivered, contract not ended) - the colours of the enum's „Aktiv". */
export const BM_CONTRACT_STATUS_FALLBACK_ACTIVE_STYLE_CLASS = bmEnumTagStyleClass('green-200', 'green-800');

/** Fallback tag „Beendet" (no base status delivered, contract ended) - neutral. */
export const BM_CONTRACT_STATUS_FALLBACK_ENDED_STYLE_CLASS = bmEnumTagStyleClass(null, null);

/** Tag „ab {Datum}" of an assignment that has not started yet - informational blue. */
export const BM_CONTRACT_LINK_FUTURE_TAG_STYLE_CLASS = bmEnumTagStyleClass('blue-100', 'blue-800');

/** Tag „bis {Datum}" of an assignment this account no longer bills - neutral. */
export const BM_CONTRACT_LINK_ENDED_TAG_STYLE_CLASS = bmEnumTagStyleClass(null, null);
